/* v-fp: final price badge (spec docs/superpowers/specs/2026-06-12-final-price-badge-design.md §5.2)
   v3 (2026-07-20): multi-currency fix — probe passes @inContext country (was: always shop-default
   market, i.e. USD save/currency on EUR/GBP storefronts); locale-aware base parse (was: text parse
   that stripped EU decimal commas, "€210,95"→21095 → rendered "$21,025.00"); labels formatted with
   the theme's active moneyFormat instead of hardcoded en-US Intl.
   v3.1 (2026-07-20): base reads displayed TEXT first, data-price attr only as fallback — the tab
   clone's text is the sole carrier of the accessory bundle total (main-product.liquid
   updatePriceTotals() rewrites innerHTML without refreshing data-price, which after a theme.js
   variant rerender exists but is product-only), so attr-first dropped accessories from the tab
   Final; parsePrice adopts card-fp splitNum semantics (last [.,] + 1-2 trailing digits = decimal).
   v2 (2026-06-12): Final tracks the LIVE displayed base price and updates on variant/accessory
   change via MutationObserver — no probe-latency or mouse dependency.
   - human-signal gate + webdriver check; variant:changed only re-probes when the gate is already open
   - same-origin tokenless Storefront cartCreate probe -> discount amount (save) only
   - Final = (current displayed base price read from theme DOM) - save, rendered to <canvas>
   - main badge tracks product price; tab badge tracks bundle (product + accessories) price
   - SAVE banner shows discount amount (save); accessories do not change it
   - in-memory cache only, no Web Storage; canvas unavailable -> render nothing (never leak text) */
(() => {
  'use strict';
  if (window.__vfpInit) return;
  window.__vfpInit = true;

  const cfgEl = document.getElementById('v-fp-config');
  if (!cfgEl) return;
  let cfg;
  try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }
  if (!cfg || !cfg.v || !Array.isArray(cfg.vs) || !cfg.api) return;

  // v3: buyer country for the probe's @inContext — without it cartCreate prices in the shop-default
  // market (USD), and non-US storefronts get a USD save + "$" badge over an EUR/GBP base price.
  // cfg.country is injected by v-fp.liquid; themeVariables.localizationCountry covers a stale snippet.
  const COUNTRY = cfg.country
    || (window.themeVariables && window.themeVariables.settings
        && window.themeVariables.settings.localizationCountry)
    || null;
  const PAGE_CUR = window.themeVariables && window.themeVariables.settings
    ? window.themeVariables.settings.cartCurrency || null
    : null;

  const SEL_MAIN_CONT = '.product-overview .product-meta__price-list-container';
  const SEL_TAB_WRAPS = '.product-detail-tab .price-list-wrapper';
  const API = location.origin + '/api/' + cfg.api + '/graphql.json';
  const QUERY = 'mutation P($lines:[CartLineInput!]!,$country:CountryCode)@inContext(country:$country){cartCreate(input:{lines:$lines}){cart{id lines(first:20){edges{node{merchandise{... on ProductVariant{id}}cost{subtotalAmount{amount currencyCode}totalAmount{amount currencyCode}}discountAllocations{discountedAmount{amount currencyCode}targetType ... on CartAutomaticDiscountAllocation{title}}}}}}userErrors{field message}}}';
  const ARIA = 'A discounted price is applied automatically at checkout';
  const CLOCK = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="13" r="8"/><path d="M12 9v4l2.5 2.5M9 2h6"/></svg>';
  const FONT = "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif";

  const cache = new Map();
  const fp = new WeakMap();   // node -> render fingerprint. WeakMap is closure-private and non-enumerable,
                              // so the final price is never exposed as a readable element property (anti-scrape).
  let data = null;              // { save:Number, cur:String } | null
  let activeVariant = Number(cfg.v);
  let gateOpen = false;
  let probeSeq = 0;
  let debounceT = null;
  let styleEl = null;
  let mo = null;
  let scheduled = false;

  // v3: format like the theme does. js-variables exposes shop.money_format for the ACTIVE presentment
  // currency ("€{{amount_with_comma_separator}}" on EUR markets, "${{amount}}" on US) — using it keeps
  // the badge stylistically identical to the theme-rendered struck price ("€140,95", not "$140.95").
  // theme.js has the same formatter but module-private, so a minimal copy lives here.
  function formatWithDelimiters(cents, precision, thousands, decimal) {
    const number = (cents / 100).toFixed(precision);
    const parts = number.split('.');
    const head = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return head + (parts[1] ? decimal + parts[1] : '');
  }
  const MONEY_STYLES = {   // placeholder -> [thousands, decimal, forced precision?]
    amount: [',', '.'], amount_no_decimals: [',', '.', 0],
    amount_with_comma_separator: ['.', ','], amount_no_decimals_with_comma_separator: ['.', ',', 0],
    amount_with_space_separator: [' ', '.'], amount_no_decimals_with_space_separator: [' ', '.', 0],
    amount_with_apostrophe_separator: ["'", '.'], amount_no_decimals_with_apostrophe_separator: ["'", '.', 0]
  };
  function money(n, cur, trimWhole) {
    const whole = trimWhole && Math.abs(n - Math.round(n)) < 0.005;
    try {
      const tv = window.themeVariables;
      const fmt = tv && tv.settings && tv.settings.moneyFormat;
      const m = fmt ? String(fmt).match(/\{\{\s*(\w+)\s*\}\}/) : null;
      const style = m && MONEY_STYLES[m[1]];
      if (style) {
        const precision = style.length > 2 ? 0 : (whole ? 0 : 2);
        const value = formatWithDelimiters(Math.round(n * 100), precision, style[0], style[1]);
        return String(fmt).replace(/<[^>]*>/g, '').replace(m[0], value).trim();
      }
    } catch (e) { /* fall through to Intl */ }
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency', currency: cur || 'USD',
        minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2
      }).format(n);
    } catch (e) { return '$' + n.toFixed(2); }
  }

  function canvasOK() {
    try {
      const c = document.createElement('canvas');
      return !!(c.getContext && c.getContext('2d'));
    } catch (e) { return false; }
  }

  // 基线对齐版：文字画在 canvas 底边附近，配 vertical-align:baseline / flex baseline，使 canvas 文字基线
  // 与相邻文本（划线原价）基线对齐——修复"折后价偏高"。价格只有 $0-9 无下伸字母，无需 descent 空间。
  function canvasText(text, px, color, weight) {
    const dpr = Math.max(window.devicePixelRatio || 1, 1);
    const c = document.createElement('canvas');
    const ctx = c.getContext('2d');
    if (!ctx) return c;
    const font = (weight || 700) + ' ' + px + 'px ' + FONT;
    ctx.font = font;
    const w = Math.ceil(ctx.measureText(text).width) + 2;
    const cap = Math.ceil(px * 0.74);     // 基线以上字高
    const pad = Math.ceil(px * 0.16);     // 顶部留白防裁切
    const h = pad + cap + 1;              // 基线贴底（底下留 1px）→ baseline 对齐相邻文本
    c.width = Math.ceil(w * dpr);
    c.height = Math.ceil(h * dpr);
    c.style.width = w + 'px';
    c.style.height = h + 'px';
    c.style.verticalAlign = 'baseline';
    c.setAttribute('aria-hidden', 'true');
    ctx.scale(dpr, dpr);
    ctx.font = font;
    ctx.fillStyle = color;
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(text, 1, pad + cap);
    return c;
  }

  function ensureStyle() {
    if (styleEl) return;
    const s = document.createElement('style');
    s.id = 'v-fp-css';
    s.textContent = [
      // 低调版（2026-06-27）：深色折后价(canvas，主价、在前) + 灰划线原价(主题DOM，在中) + 小淡红 Save chip(canvas，在后) + 灰提示小字。
      '.v-fp-final{display:inline-flex;align-items:baseline;margin-right:8px}',
      '.v-fp-chip{display:inline-flex;align-items:center;background:#FDECEE;border-radius:6px;padding:2px 8px;margin-left:8px}',
      // 划线原价强制灰色（!important 压过主题 .price--highlight 的红/橙；修 Codex MINOR：高亮促销价情形）
      '.v-fp-struck{text-decoration:line-through!important;font-weight:400;font-size:15px!important;color:#959595!important;-webkit-text-fill-color:#959595!important}',
      '.v-fp-note{font-size:12px;color:#8a8a8a;margin:6px 0 2px;opacity:0;transition:opacity .2s ease}',
      '.v-fp-note.v-fp-in{opacity:1}',
      // tab 栏折后价：去红盒、深色、上移 2px（Bill）
      '.v-fp-tabb{display:inline-flex;align-items:center;margin-right:10px;vertical-align:middle;position:relative;top:-2px}'
    ].join('');
    document.head.appendChild(s);
    styleEl = s;
  }

  function prefix() {
    let p = String(cfg.t1 || '');
    if (p && !/\s$/.test(p)) p += ' ';
    return p;
  }

  // v3.1 locale-aware textual parse, same rule as card-fp splitNum: the LAST [.,] followed by
  // 1-2 digits is the decimal mark; a 3+ digit tail means thousands. "€210,95"→210.95,
  // "$1,199.99"→1199.99, "€1.234,56"→1234.56, "$1,199"→1199, "¥1.234"→1234.
  function parsePrice(text) {
    if (!text) return NaN;
    const s = String(text).replace(/[^\d.,]/g, '');
    if (!s) return NaN;
    const li = Math.max(s.lastIndexOf(','), s.lastIndexOf('.'));
    const frac = li >= 0 ? s.length - li - 1 : 0;
    const dec = (frac === 1 || frac === 2) ? frac : 0;
    const n = parseInt(s.replace(/[.,]/g, ''), 10);
    if (!Number.isFinite(n)) return NaN;
    return dec > 0 ? n / Math.pow(10, dec) : n;
  }
  // Read the theme-maintained current selling price inside a scope (first .price in its .price-list).
  // v3.1: displayed TEXT first — the badge must agree with the price the shopper sees, and the tab
  // clone's text is the only carrier of the bundle (product+accessories) total: main-product.liquid
  // updatePriceTotals() rewrites the bottom-price-list span's innerHTML WITHOUT refreshing its
  // data-price attribute (attr appears only after a theme.js product-meta rerender, and then holds
  // the product-only price). data-price is the fallback for unparseable text only.
  function readBase(scope) {
    if (!scope) return NaN;
    const pl = scope.querySelector('.price-list');
    if (!pl) return NaN;
    const priceEl = pl.querySelector('.price');
    if (!priceEl) return NaN;
    const n = parsePrice(priceEl.textContent);
    if (n > 0) return n;
    const cents = parseInt(priceEl.getAttribute('data-price'), 10);
    return Number.isFinite(cents) && cents > 0 ? cents / 100 : NaN;
  }

  // 顺序：折后价(final) 在原价之前、Save chip 在原价之后 → "final ~~struck~~ chip"（与卡片一致，Bill）。
  // 复用 .v-fp-final / .v-fp-chip 节点，仅在值变化时换 canvas（防抖、防闪）；价格数字仍 canvas（防爬）。
  function paintMain(cont, show) {
    const pl = cont.querySelector('.price-list');
    const priceEl = pl ? pl.querySelector('.price') : null;   // theme 当前售价(=base)；只加 CSS 划线，textContent 不变
    let finEl = pl ? pl.querySelector(':scope > .v-fp-final') : null;
    let chipEl = pl ? pl.querySelector(':scope > .v-fp-chip') : null;
    if (!show || !pl || !priceEl) {
      if (finEl) finEl.remove();
      if (chipEl) chipEl.remove();
      if (priceEl) priceEl.classList.remove('v-fp-struck');
      return;
    }
    const base = readBase(cont);
    if (!(base > 0)) {
      if (finEl) finEl.remove();
      if (chipEl) chipEl.remove();
      priceEl.classList.remove('v-fp-struck');
      return;
    }
    ensureStyle();
    priceEl.classList.add('v-fp-struck');        // 原价改成灰划线（仅样式，readBase 仍可读原数字）
    const finalLabel = money(base - data.save, data.cur, false);     // 折后价(深色 canvas，主价)
    const saveLabel = 'Save ' + money(data.save, data.cur, true);    // Save chip(淡红 canvas)
    if (!finEl) {
      finEl = document.createElement('span');
      finEl.className = 'v-fp-final';
      finEl.setAttribute('role', 'img');
      finEl.setAttribute('aria-label', ARIA);
    }
    if (!chipEl) {
      chipEl = document.createElement('span');
      chipEl.className = 'v-fp-chip';
    }
    if (priceEl.previousElementSibling !== finEl) priceEl.insertAdjacentElement('beforebegin', finEl);   // final 在原价之前
    if (priceEl.nextElementSibling !== chipEl) priceEl.insertAdjacentElement('afterend', chipEl);        // chip 在原价之后
    const sig = base + ',' + data.save;   // redraw when base OR discount changes; private WeakMap, not on the node
    if (fp.get(finEl) !== sig) {
      finEl.textContent = '';
      finEl.appendChild(canvasText(finalLabel, 22, '#1c1b1b', 600));   // 深色墨黑折后价
      chipEl.textContent = '';
      chipEl.appendChild(canvasText(saveLabel, 12, '#C20010', 600));   // 淡红 Save 金额
      fp.set(finEl, sig);
    }
  }

  // 低调版：原大橙 SAVE 横幅 → 价格下方一行灰色提示小字（纯文案、无数字，零泄漏面）。
  function paintNote(cont, show) {
    let note = document.querySelector('.v-fp-note');
    if (!show || !cont.parentNode) { if (note) note.remove(); return; }
    ensureStyle();
    if (!note) {
      note = document.createElement('div');
      note.className = 'v-fp-note';
      cont.parentNode.insertBefore(note, cont.nextSibling);
      requestAnimationFrame(() => requestAnimationFrame(() => note.classList.add('v-fp-in')));
    }
    const t2 = String(cfg.t2 || '');   // 设置项文案，禁含数字（settings_schema 已注明）
    const t3 = String(cfg.t3 || '');
    note.textContent = t2 + (t2 && t3 ? '  ' : '') + t3;   // 空格分隔（t2 已带句号，去掉多余的「·」）
  }

  // Tab Final = bundle base price (product + accessories, read from theme DOM) − data.save (product
  // discount). Assumes accessories carry no automatic discount (VIOFO norm; verified OP100 save=0). If an
  // accessory ever gets one, the tab Final would be high by that amount — accepted bounded approximation.
  function paintTab(w, show) {
    let b = w.querySelector(':scope > .v-fp-tabb');
    const priceEl = w.querySelector('.price-list .price');   // 标签栏原价；只加 CSS 划线
    if (!show) { if (b) b.remove(); if (priceEl) priceEl.classList.remove('v-fp-struck'); return; }
    const base = readBase(w);
    if (!(base > 0)) { if (b) b.remove(); if (priceEl) priceEl.classList.remove('v-fp-struck'); return; }
    ensureStyle();
    if (priceEl) priceEl.classList.add('v-fp-struck');       // 标签栏原价灰划线
    const label = money(base - data.save, data.cur, false);  // 深色折后(canvas)，无红盒、无 "Final:" 前缀
    if (!b) {
      b = document.createElement('span');
      b.className = 'v-fp-tabb';
      b.setAttribute('role', 'img');
      b.setAttribute('aria-label', ARIA);
    }
    const sig = base + ',' + data.save;   // redraw when bundle base OR discount changes; private WeakMap
    if (fp.get(b) !== sig) {
      b.textContent = '';
      b.appendChild(canvasText(label, 18, '#1c1b1b', 600));   // tab 折后价加大（Bill：tab 实际价大一点）
      fp.set(b, sig);
    }
    const plw = w.querySelector('.price-list');         // 折后价(b) 放在原价(.price-list) 之前 → "final ~~struck~~"（与主价区一致，Bill）
    if (plw) { if (b.nextElementSibling !== plw) w.insertBefore(b, plw); }
    else if (w.firstElementChild !== b) w.insertBefore(b, w.firstElementChild);
  }

  // Re-render all badges from current `data` + the live DOM base prices. Idempotent.
  function renderAll() {
    if (mo) mo.disconnect();
    try {
      const show = !!(data && data.save > 0 && canvasOK());
      const cont = document.querySelector(SEL_MAIN_CONT);
      if (cont) { paintMain(cont, show); paintNote(cont, show); }
      else { const note = document.querySelector('.v-fp-note'); if (note) note.remove(); }
      document.querySelectorAll(SEL_TAB_WRAPS).forEach((w) => paintTab(w, show));
    } catch (e) { /* never throw into the page */ }
    finally { connectMo(); }
  }

  function scheduleRender() {
    // queueMicrotask (NOT rAF): runs after the theme's synchronous setTabPrice (which appends the
    // recloned .price-list AFTER our badge, momentarily putting the badge on the left) but BEFORE the
    // browser repaints — so we move the badge back to the end within the same frame and the
    // intermediate "badge on the left" state is never painted (no left/right jitter). See repro-jitter2.
    if (scheduled) return;
    scheduled = true;
    queueMicrotask(() => { scheduled = false; renderAll(); });
  }

  // Observe theme-driven price DOM changes (variant rerender, accessory setTabPrice reclone).
  // Observes the CURRENT container/wrapper nodes. The theme rebuilds the inner .price-list, not the
  // container itself, so this stays attached. If a future theme fully replaces
  // .product-meta__price-list-container, the observer would go stale and need reattachment.
  function connectMo() {
    if (!gateOpen) return;
    if (!mo) mo = new MutationObserver(scheduleRender);
    else mo.disconnect();
    const cont = document.querySelector(SEL_MAIN_CONT);
    if (cont) mo.observe(cont, { childList: true, subtree: true });
    document.querySelectorAll(SEL_TAB_WRAPS).forEach((w) => mo.observe(w, { childList: true }));
  }

  async function probe(variantId) {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 5000);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (cfg.k) headers['X-Shopify-Storefront-Access-Token'] = cfg.k;
      const res = await fetch(API, {
        method: 'POST',
        headers,
        signal: ctrl.signal,
        body: JSON.stringify({
          query: QUERY,
          variables: { lines: [{ merchandiseId: 'gid://shopify/ProductVariant/' + variantId, quantity: 1 }], country: COUNTRY }
        })
      });
      if (!res.ok) return null;
      const json = await res.json();
      const cc = json && json.data && json.data.cartCreate;
      if (!cc || (cc.userErrors && cc.userErrors.length) || !cc.cart) return null;
      const edge = cc.cart.lines && cc.cart.lines.edges && cc.cart.lines.edges[0];
      if (!edge || !edge.node) return null;
      const cur = edge.node.cost && edge.node.cost.subtotalAmount
        ? edge.node.cost.subtotalAmount.currencyCode : 'USD';
      if (cur && PAGE_CUR && cur !== PAGE_CUR) return null;
      let save = 0;
      (edge.node.discountAllocations || []).forEach((a) => {
        if (a && a.targetType === 'LINE_ITEM' && a.discountedAmount) {
          save += parseFloat(a.discountedAmount.amount);
        }
      });
      return { save: save > 0.009 ? save : 0, cur: cur };
    } catch (e) {
      return null;
    } finally {
      clearTimeout(timer);
    }
  }

  // NOTE (bounded stale-save window): data.save is the active variant's discount. On variant switch the
  // theme updates the DOM base price synchronously and the MutationObserver may render (new base − PREVIOUS
  // save) before this probe resolves. Harmless when all variants of a product share one discount amount
  // (VIOFO's automatic discount applies to every variant => save is constant), and a cache hit updates save
  // synchronously with no window. If variants ever carry different discount amounts, Final is briefly off by
  // the delta for ≤(150ms debounce + network) and self-corrects when the probe returns.
  // Backoff (ms) for retrying a FAILED probe. probe() returns null only on failure (HTTP non-OK,
  // userErrors, timeout, network) — the tokenless Storefront API is rate-limited, and a card-fp burst on
  // the same page (shared /api/.../graphql.json) can throttle this single PDP probe. A null result must
  // NOT be cached (it is "unknown", not "no discount"); otherwise the badge stays absent for the whole
  // session since the human-signal gate fires only once. Retrying with backoff lets a throttled first
  // probe self-heal. probe() returning {save:0} IS authoritative no-discount and is cached (no retry).
  const RETRY_DELAYS = [1500, 4000];

  async function probeAndRender(variantId, attempt) {
    const seq = ++probeSeq;
    let d;
    if (cache.has(variantId)) d = cache.get(variantId);
    else {
      d = await probe(variantId);
      if (d) cache.set(variantId, d);   // cache only authoritative results; null = throttle/unknown -> never cache
    }
    if (seq !== probeSeq || variantId !== activeVariant) return;
    data = d;
    renderAll();                          // d == null -> badge removed (safe: never show another variant's stale save)
    if (d == null) {                      // failed probe (likely rate-limited) -> retry with backoff, then give up
      const a = attempt || 0;
      if (a < RETRY_DELAYS.length) setTimeout(() => {
        if (variantId === activeVariant && !cache.has(variantId)) probeAndRender(variantId, a + 1);
      }, RETRY_DELAYS[a]);
    }
  }

  function openGate() {
    if (gateOpen) return false;
    if (navigator.webdriver === true) return false;
    gateOpen = true;
    connectMo();
    return true;
  }

  document.addEventListener('variant:changed', (ev) => {
    const v = ev && ev.detail && ev.detail.variant;
    if (!v || cfg.vs.indexOf(v.id) === -1) return;
    if (!(ev.target && ev.target.closest && ev.target.closest('.product-overview'))) return;
    activeVariant = v.id;
    // Do NOT open the gate here: variant:changed can be synthesised by a script (it only proves a valid
    // variant id bubbling inside .product-overview, not a human). The gate stays driven solely by the raw
    // human-interaction signals below. A real user operating the variant selector always produces a
    // mousemove/touch first, so the gate is already open in practice when this fires.
    if (!gateOpen) return;
    clearTimeout(debounceT);
    debounceT = setTimeout(() => { probeAndRender(activeVariant); }, 150);
  });

  const SIGNALS = ['mousemove', 'touchstart', 'scroll', 'keydown'];
  function onSignal() {
    SIGNALS.forEach((s) => window.removeEventListener(s, onSignal));
    if (openGate()) probeAndRender(activeVariant);
  }
  SIGNALS.forEach((s) => window.addEventListener(s, onSignal, { once: true, passive: true }));
})();
